import { useMemo, useState, type FormEvent } from 'react';

import { IconCheck, IconClose, IconEdit, IconEye, IconRefreshCw, IconSearch, IconTrash } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { useOpdLookup } from '../opd/opd.queries';
import { lastMonthDateFilters, todayIso } from './report-filters';
import {
  useApproveCommission,
  useCommissionReport,
  useCreateCommission,
  useDeleteCommission,
  usePayCommission,
  useUpdateCommission,
} from './reports.queries';
import type { CommissionLineInput, CommissionReportFilters, CommissionReportRow, CommissionTestLine } from './types';

const initialFilters = (): CommissionReportFilters => ({
  page: 1,
  limit: 25,
  ...lastMonthDateFilters(),
  referral: '',
  value: '',
});

const formatAmount = (value: number) => value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatFilterDate = (value: string | undefined) => value ? value.split('-').reverse().join('-') : '';
const patientText = (name: string, uhid: string | null) => `${name}${uhid ? ` (${uhid})` : ''}`.toUpperCase();
const mutationMessage = (error: unknown) => error instanceof ApiError ? error.message : 'Commission action could not be completed.';

type EditorMode = 'create' | 'view' | 'edit';
interface EditorState { row: CommissionReportRow; mode: EditorMode }

const CommissionEditor = ({ editor, lines, pending, onLinesChange, onSave, onClose }: {
  editor: EditorState | null;
  lines: CommissionTestLine[];
  pending: boolean;
  onLinesChange: (lines: CommissionTestLine[]) => void;
  onSave: () => void;
  onClose: () => void;
}) => {
  const readOnly = editor?.mode === 'view';
  const total = useMemo(() => lines.reduce((sum, line) => sum + line.commissionAmount, 0), [lines]);
  const updatePercentage = (index: number, value: number) => onLinesChange(lines.map((line, currentIndex) => currentIndex === index ? {
    ...line,
    commissionPercentage: value,
    commissionAmount: Number((line.testAmount * value / 100).toFixed(2)),
  } : line));
  const updateAmount = (index: number, value: number) => onLinesChange(lines.map((line, currentIndex) => currentIndex === index ? {
    ...line,
    commissionAmount: value,
    commissionPercentage: line.testAmount > 0 ? Number((value * 100 / line.testAmount).toFixed(4)) : 0,
  } : line));

  return (
    <Modal open={editor !== null} onClose={() => { if (!pending) onClose(); }} labelledBy="commission-editor-title" className="commission-editor-modal">
      <header className="commission-editor-header">
        <div>
          <h2 id="commission-editor-title">Patient Test Report</h2>
          {editor ? <span>{editor.row.billId} · {patientText(editor.row.patientName, editor.row.uhid)}</span> : null}
        </div>
        <button type="button" onClick={onClose} disabled={pending} aria-label="Close commission"><IconClose /></button>
      </header>
      <div className="commission-editor-body">
        <div className="commission-editor-table-scroll">
          <table className="commission-editor-table">
            <thead><tr><th>#</th><th>Bill ID</th><th>Test Name</th><th>Test Amount</th><th>Commission (%)</th><th>Commission Amount</th></tr></thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.chargeKey}>
                  <td>{index + 1}</td>
                  <td>{editor?.row.billId}</td>
                  <td>{line.testName.toUpperCase()}</td>
                  <td>₹ {formatAmount(line.testAmount)}</td>
                  <td><input aria-label={`${line.testName} commission percentage`} type="number" min={0} max={100} step="0.0001" value={line.commissionPercentage} readOnly={readOnly} onChange={(event) => updatePercentage(index, Math.min(100, Math.max(0, Number(event.target.value))))} /></td>
                  <td><input aria-label={`${line.testName} commission amount`} type="number" min={0} max={line.testAmount} step="0.01" value={line.commissionAmount} readOnly={readOnly} onChange={(event) => updateAmount(index, Math.min(line.testAmount, Math.max(0, Number(event.target.value))))} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={5}>Total Commission</td><td><input aria-label="Total commission" readOnly value={total.toFixed(2)} /></td></tr></tfoot>
          </table>
        </div>
      </div>
      <footer className="commission-editor-footer">
        {!readOnly ? <button className="commission-save-button" type="button" onClick={onSave} disabled={pending}>{pending ? 'Saving…' : 'Save Commission'}</button> : null}
        <button className="commission-close-button" type="button" onClick={onClose} disabled={pending}>Close</button>
      </footer>
    </Modal>
  );
};

export const CommissionReportPage = () => {
  const [draft, setDraft] = useState<CommissionReportFilters>(initialFilters);
  const [filters, setFilters] = useState<CommissionReportFilters>(initialFilters);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [lines, setLines] = useState<CommissionTestLine[]>([]);
  const { showToast } = useToast();
  const referrals = useOpdLookup('referrals');
  const query = useCommissionReport(filters);
  const createMutation = useCreateCommission();
  const updateMutation = useUpdateCommission();
  const deleteMutation = useDeleteCommission();
  const approveMutation = useApproveCommission();
  const payMutation = usePayCommission();
  const totalPages = Math.max(1, query.data?.pagination.totalPages ?? 1);
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Commission report could not be loaded.';
  const editorPending = createMutation.isPending || updateMutation.isPending;

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1 });
  };

  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };

  const openEditor = (row: CommissionReportRow, mode: EditorMode) => {
    setEditor({ row, mode });
    setLines((mode === 'create' ? row.tests : row.commission?.lines ?? row.tests).map((line) => ({ ...line })));
  };

  const saveCommission = async () => {
    if (!editor) return;
    const payload: CommissionLineInput[] = lines.map(({ chargeKey, commissionPercentage, commissionAmount }) => ({ chargeKey, commissionPercentage, commissionAmount }));
    try {
      if (editor.mode === 'create') await createMutation.mutateAsync({ module: editor.row.module, sourceId: editor.row.sourceId, lines: payload });
      else if (editor.row.commission) await updateMutation.mutateAsync({ id: editor.row.commission.id, lines: payload });
      showToast('Commission saved successfully.');
      setEditor(null);
    } catch (error) {
      showToast(mutationMessage(error), 'error');
    }
  };

  const deleteCommission = async (row: CommissionReportRow) => {
    if (!row.commission || !await confirmAction({ title: 'Delete Commission', description: `Delete the commission for bill ${row.billId}?`, confirmLabel: 'Delete', tone: 'danger' })) return;
    try {
      await deleteMutation.mutateAsync(row.commission.id);
      showToast('Commission deleted.');
    } catch (error) {
      showToast(mutationMessage(error), 'error');
    }
  };

  const approveCommission = async (row: CommissionReportRow) => {
    if (!row.commission || !await confirmAction({ title: 'Approve Commission', description: `Approve ₹${formatAmount(row.commission.totalCommission)} for bill ${row.billId}? Approved commissions cannot be edited or deleted.`, confirmLabel: 'Approve' })) return;
    try {
      await approveMutation.mutateAsync(row.commission.id);
      showToast('Commission approved.');
    } catch (error) {
      showToast(mutationMessage(error), 'error');
    }
  };

  const payCommission = async (row: CommissionReportRow) => {
    if (!row.commission || !await confirmAction({ title: 'Pay Commission', description: `Mark ₹${formatAmount(row.commission.totalCommission)} as paid for bill ${row.billId}?`, confirmLabel: 'Pay' })) return;
    try {
      await payMutation.mutateAsync(row.commission.id);
      showToast('Commission marked as paid.');
    } catch (error) {
      showToast(mutationMessage(error), 'error');
    }
  };

  const changePage = (page: number) => setFilters((current) => ({ ...current, page }));
  const period = filters.fromDate || filters.toDate ? `${formatFilterDate(filters.fromDate) || 'Earliest'} to ${formatFilterDate(filters.toDate) || 'Latest'}` : 'All dates';

  return (
    <div className="page-stack commission-report-page">
      <section className="content-card commission-report-card">
        <header className="general-settings-titlebar"><h2>Commission Report</h2></header>
        <form className="commission-report-filters report-print-hidden" onSubmit={applyFilters}>
          <label><span>Referral By</span><select value={draft.referral ?? ''} onChange={(event) => setDraft((current) => ({ ...current, referral: event.target.value }))}><option value="">All Referrals</option>{(referrals.data ?? []).map((referral) => <option key={referral.id} value={referral.id}>{referral.label}</option>)}</select></label>
          <label><span>From Date</span><input type="date" value={draft.fromDate ?? ''} max={draft.toDate || todayIso()} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} /></label>
          <label><span>To Date</span><input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} max={todayIso()} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} /></label>
          <div className="commission-report-actions"><button className="report-toolbar-btn search" type="submit" disabled={query.isFetching}><IconSearch />Search</button><button className="report-toolbar-btn reset" type="button" onClick={reset}><IconRefreshCw />Reset</button></div>
        </form>
      </section>

      <section className="content-card commission-report-table-card">
        {query.isLoading ? <LoadingState rows={10} columns={7} /> : null}
        {query.isError ? <ErrorState title="Unable to load commission report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && query.data.data.length === 0 ? <EmptyState title="No bills found" message="Change the referral or date range and search again." /> : null}
        {query.isSuccess && query.data.data.length > 0 ? (
          <>
            <div className="commission-report-table-scroll">
              <table className="commission-report-table">
                <thead><tr><th>#</th><th>Billing ID</th><th>Date</th><th>Patient</th><th>Lab Amount</th><th>Commission Amount</th><th>Action</th></tr><tr className="commission-report-summary-row"><th colSpan={7}>Total Patients: {query.data.summary.count.toLocaleString('en-IN')} <span>|</span> Date: {period}</th></tr></thead>
                <tbody>
                  {query.data.data.map((row, index) => (
                    <tr key={row.id}>
                      <td>{(query.data.pagination.page - 1) * query.data.pagination.limit + index + 1}</td>
                      <td><strong>{row.billId}</strong><span className="commission-source-badge">{row.sourceSection}</span></td>
                      <td>{formatIpdDateTime(row.billDate)}</td>
                      <td>{patientText(row.patientName, row.uhid)}</td>
                      <td>₹ {formatAmount(row.labAmount)}</td>
                      <td>₹ {formatAmount(row.commissionAmount)}</td>
                      <td><div className="commission-row-actions">
                        {!row.commission ? <button className="create" type="button" onClick={() => openEditor(row, 'create')}>Create</button> : null}
                        {row.commission ? <button className="view" type="button" onClick={() => openEditor(row, 'view')}><IconEye />View</button> : null}
                        {row.commission?.status === 'draft' ? <button className="edit" type="button" onClick={() => openEditor(row, 'edit')}><IconEdit />Edit</button> : null}
                        {row.commission?.status === 'draft' ? <button className="delete" type="button" disabled={deleteMutation.isPending} onClick={() => void deleteCommission(row)}><IconTrash />Delete</button> : null}
                        {row.commission?.status === 'draft' ? <button className="approve" type="button" disabled={approveMutation.isPending} onClick={() => void approveCommission(row)}><IconCheck />Approve</button> : null}
                        {row.commission?.status === 'approved' ? <span className="commission-approved-badge"><IconCheck /><span>Approved{row.commission.approvedAt ? <time dateTime={row.commission.approvedAt}>{formatIpdDateTime(row.commission.approvedAt)}</time> : null}</span></span> : null}
                        {row.commission?.status === 'approved' ? <button className="pay" type="button" disabled={payMutation.isPending} onClick={() => void payCommission(row)}><IconCheck />Pay</button> : null}
                        {row.commission?.status === 'paid' ? <span className="commission-paid-badge"><IconCheck /><span>Paid{row.commission.approvedAt ? <time dateTime={row.commission.approvedAt}>Approved: {formatIpdDateTime(row.commission.approvedAt)}</time> : null}{row.commission.paidAt ? <time dateTime={row.commission.paidAt}>Paid: {formatIpdDateTime(row.commission.paidAt)}</time> : null}</span></span> : null}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4}>Total</td><td>₹ {formatAmount(query.data.summary.labAmount)}</td><td>₹ {formatAmount(query.data.summary.commissionAmount)}</td><td /></tr></tfoot>
              </table>
            </div>
            <div className="pagination commission-report-pagination report-print-hidden"><span className="pagination-summary">Showing {((query.data.pagination.page - 1) * query.data.pagination.limit + 1).toLocaleString('en-IN')} to {Math.min(query.data.pagination.page * query.data.pagination.limit, query.data.pagination.total).toLocaleString('en-IN')} of {query.data.pagination.total.toLocaleString('en-IN')} entries</span><button className="button button-secondary" type="button" disabled={filters.page <= 1} onClick={() => changePage(filters.page - 1)}>Previous</button><span>Page {filters.page} of {totalPages}</span><button className="button button-secondary" type="button" disabled={filters.page >= totalPages} onClick={() => changePage(filters.page + 1)}>Next</button></div>
          </>
        ) : null}
      </section>
      <CommissionEditor editor={editor} lines={lines} pending={editorPending} onLinesChange={setLines} onSave={() => void saveCommission()} onClose={() => setEditor(null)} />
    </div>
  );
};
